import {
  isSupportedCountry,
  isValidPhoneNumber as libIsValidPhoneNumber,
  formatPhoneNumberIntl,
  parsePhoneNumber,
  type Country,
} from "react-phone-number-input";

/**
 * Resolves a country code to a PhoneInput-compatible Country value.
 * Always falls back to "US" so the phone component never inherits the
 * browser locale (which can silently default to e.g. CN/+86 for
 * Chinese-language browsers).
 */
export function phoneDefaultCountry(code: string): Country {
  return code && isSupportedCountry(code) ? (code as Country) : "US";
}

/**
 * Validates an E.164 phone number string (e.g. "+14155551234").
 * Returns true for empty/undefined (optional field).
 */
export function isValidPhoneE164(value: string | null | undefined): boolean {
  if (!value) return true;
  try {
    return libIsValidPhoneNumber(value);
  } catch {
    return false;
  }
}

/** True once the value has enough digits to judge (E.164 usable numbers are typically 8–15). */
export function isPhoneInputComplete(value: string): boolean {
  return value.replace(/\D/g, "").length >= 8;
}

/**
 * Inline error for phone fields. Returns undefined while the user is still
 * typing so we don't flash "invalid" on every partial keystroke.
 */
export function phoneFieldError(value: string | null | undefined): string | undefined {
  if (!value || !isPhoneInputComplete(value)) return undefined;
  if (isValidPhoneE164(value)) return undefined;
  return "Enter a valid phone number";
}

/**
 * Normalizes a phone string to E.164 for use with PhoneInputField.
 * - Already-E.164 values (starting with "+") are returned as-is.
 * - Legacy 10-digit US-only strings (digits, parens, dashes, spaces)
 *   are converted to "+1XXXXXXXXXX".
 * - Anything else is returned as-is so the input can display it and
 *   validation can surface the error.
 */
export function normalizeToE164(value: string | null | undefined): string {
  if (!value) return "";
  const v = value.trim();
  if (!v) return "";
  if (v.startsWith("+")) return v;
  const digits = v.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return v;
}

/**
 * Displays a phone number in a readable international format.
 * Handles both E.164 ("+14155551234") and legacy US format ("(415) 555-1234").
 */
export function formatPhoneDisplay(value: string | null | undefined): string {
  if (!value) return "";
  const v = value.trim();
  if (!v) return "";
  if (v.startsWith("+")) {
    return formatPhoneNumberIntl(v) || v;
  }
  const digits = v.replace(/\D/g, "");
  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return v;
}

/** Regional-indicator flag emoji for an ISO 3166-1 alpha-2 country code. */
export function countryCodeToFlagEmoji(iso2: string): string {
  const code = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return "";
  const regionalA = 0x1f1e6;
  return String.fromCodePoint(
    regionalA + (code.charCodeAt(0) - 65),
    regionalA + (code.charCodeAt(1) - 65),
  );
}

export type PhoneCountryMeta = {
  country: string;
  flag: string;
  name: string;
};

function regionName(iso2: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(iso2) ?? iso2;
  } catch {
    return iso2;
  }
}

/**
 * Country (and flag) implied by a phone number's calling code.
 * Returns null when the number is empty or the country cannot be parsed.
 */
export function phoneCountryMeta(value: string | null | undefined): PhoneCountryMeta | null {
  const normalized = normalizeToE164(value);
  if (!normalized.startsWith("+")) return null;
  try {
    const parsed = parsePhoneNumber(normalized);
    const country = parsed?.country;
    if (!country) return null;
    const flag = countryCodeToFlagEmoji(country);
    if (!flag) return null;
    return { country, flag, name: regionName(country) };
  } catch {
    return null;
  }
}

/** Phone string with a leading flag when the calling-code country is known. */
export function formatPhoneWithFlag(value: string | null | undefined): string {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  const meta = phoneCountryMeta(trimmed);
  return meta ? `${meta.flag} ${trimmed}` : trimmed;
}

// ── Legacy US-only helpers kept for backward compatibility ────────────────────
// These are still used in some admin pages that haven't been migrated yet.
// New code should use PhoneInputField + normalizeToE164 instead.

/** @deprecated Use PhoneInputField + normalizeToE164 */
export function formatPhoneInput(input: string): string {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  if (digits.length === 0) return "";
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** @deprecated Use isValidPhoneE164 */
export function isValidPhone(formatted: string): boolean {
  const digits = (formatted || "").replace(/\D/g, "");
  return digits.length === 10;
}
